/**
 * 시설 마스터
 * ---------------------------------------------------------------------------
 * 여기를 채워 두면 일지 작성 화면이 행을 자동으로 만들어 준다. 그것이 이
 * 화면의 존재 이유다 — 매일 시설명을 손으로 적지 않게 하는 것.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { Facility, FacilityDeleteCheck, FacilityInput, Site } from '@eco/shared';
import { FACILITY_TYPES, FACILITY_TYPE_LABELS, ECO_PERMISSIONS } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { can, useSession } from '../lib/session';
import { HelpH, HelpList, HelpP, PageHelp } from '../components/PageHelp';

export function FacilityPage() {
  const { siteId = '' } = useParams();
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const manage = can(session, ECO_PERMISSIONS.FACILITY_MANAGE);

  const [editing, setEditing] = useState<Facility | 'new' | null>(null);
  const [form] = Form.useForm<FacilityInput>();

  const site = useQuery<Site>({
    queryKey: ['site', siteId],
    queryFn: async () => (await api.get<Site>(`/sites/${siteId}`)).data,
    enabled: !!siteId,
  });

  const facilities = useQuery<Facility[]>({
    queryKey: ['facilities', siteId],
    queryFn: async () =>
      (await api.get<Facility[]>('/facilities', { params: { siteId, includeInactive: true } })).data,
    enabled: !!siteId,
  });

  useEffect(() => {
    if (editing === 'new') form.setFieldsValue({ siteId, sortOrder: 0 } as FacilityInput);
    else if (editing) form.setFieldsValue(editing);
  }, [editing, form, siteId]);

  const save = useMutation({
    mutationFn: async (values: FacilityInput) => {
      if (editing === 'new') return api.post('/facilities', { ...values, siteId });
      return api.patch(`/facilities/${(editing as Facility).facilityId}`, values);
    },
    onSuccess: async () => {
      message.success(editing === 'new' ? '시설을 등록했습니다.' : '수정했습니다.');
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['facilities', siteId] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const deactivate = useMutation({
    mutationFn: async (facilityId: string) =>
      api.post(`/facilities/${facilityId}/deactivate`, { removedOn: null }),
    onSuccess: async () => {
      message.success('시설을 사용 중지했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['facilities', siteId] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: async (facilityId: string) => api.delete(`/facilities/${facilityId}`),
    onSuccess: async () => {
      message.success('시설을 삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['facilities', siteId] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /**
   * 삭제 전에 일지가 몇 건이나 이 시설을 가리키는지 보여 준다.
   *
   * 일지 자체는 지워지지 않는다(시설명을 복사해 둔다). 다만 링크가 끊어져
   * 시설별 집계에서 빠지므로, 그 사실을 알고 누르게 한다.
   */
  const askRemove = async (row: Facility) => {
    let check: FacilityDeleteCheck;
    try {
      check = (await api.get<FacilityDeleteCheck>(`/facilities/${row.facilityId}/delete-check`))
        .data;
    } catch (err) {
      message.error(errorMessage(err));
      return;
    }

    const used = check.referencedLogCount > 0;
    modal.confirm({
      title: `'${row.facilityName}' 을(를) 삭제할까요?`,
      width: 520,
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      content: used ? (
        <div>
          <p style={{ marginTop: 8 }}>
            이 시설을 가리키는 일지 기록이 <b>{check.referencedLogCount.toLocaleString()}건</b>{' '}
            있습니다.
          </p>
          <p>
            일지는 지워지지 않습니다. 작성 당시의 시설명을 그대로 보관하므로 내용은 남습니다. 다만{' '}
            <b>시설과의 연결이 끊어져</b> 「이 시설의 지난달 가동시간」 같은 집계에서는 빠집니다.
          </p>
          <p style={{ marginBottom: 0 }}>
            지금은 쓰지 않지만 기록은 이어 두고 싶다면 삭제 대신 <b>「중지」</b>를 쓰세요.
          </p>
        </div>
      ) : (
        <div>
          <p style={{ marginTop: 8 }}>이 시설을 참조하는 일지가 없습니다.</p>
          <p style={{ marginBottom: 0 }}>되돌릴 수 없습니다.</p>
        </div>
      ),
      onOk: () => remove.mutateAsync(row.facilityId),
    });
  };

  return (
    <Card
      title={`시설 — ${site.data?.siteName ?? ''}`}
      extra={
        <Space>
          <PageHelp title="사업장 시설관리 — 화면 설명" withLabel>
            <HelpP>
              이 화면은 <b>일지에 나올 시설을 미리 정해 두는 곳</b>입니다. 여기에 등록한
              시설이 대기 운영기록부와 폐수 자가운영일지의 표에 자동으로 채워집니다.
            </HelpP>
            <HelpP>
              등록하지 않으면 일지를 열었을 때 표가 비어 있습니다. 그러면 매일 시설명을
              손으로 적어야 하고, 오타가 나면 같은 시설이 둘로 갈라져 「이 시설의 지난달
              가동시간」 같은 조회가 맞지 않게 됩니다.
            </HelpP>

            <HelpH>시설 구분에 따라 나타나는 일지가 다릅니다</HelpH>
            <HelpList
              items={[
                { term: '대기 배출시설', desc: '대기 운영기록부 「1. 배출구별 …」 표' },
                { term: '대기 방지시설', desc: '대기 운영기록부 「2. 방지시설 운영사항」 표' },
                { term: '폐수 배출시설', desc: '폐수 자가운영일지의 배출시설 가동시간 표' },
                { term: '수질 방지시설', desc: '폐수 자가운영일지의 방지시설 가동시간 표' },
                { term: '자동측정기기', desc: 'TMS 등 자동측정 설비' },
              ]}
            />

            <HelpH>중지와 삭제는 다릅니다</HelpH>
            <HelpP>
              <b>중지</b>는 목록에서 빠지지만 행은 남습니다. 과거 일지와의 연결이 그대로라
              집계가 유지됩니다. 쓰던 시설을 더 안 쓸 때는 이쪽입니다.
              <br />
              <b>삭제</b>는 행을 지웁니다. 일지 내용은 남지만(작성 당시 시설명을 복사해
              둡니다) 연결이 끊어져 시설별 집계에서 빠집니다. 잘못 등록한 시설을 치울 때만
              쓰세요.
            </HelpP>
          </PageHelp>

          {/*
            시설 대장은 일지의 근거 자료다. 일지만 받고 시설을 못 받으면
            어느 시설의 기록인지 알 수 없게 된다 — 함께 받을 수 있어야 한다.
          */}
          <Button
            icon={<DownloadOutlined />}
            disabled={(facilities.data ?? []).length === 0}
            onClick={() =>
              downloadCsv(`시설_${site.data?.siteName ?? ''}`, facilities.data ?? [], [
                { header: '사업장', value: () => site.data?.siteName ?? '' },
                { header: '구분', value: (r) => FACILITY_TYPE_LABELS[r.facilityType] },
                { header: '배출구', value: (r) => r.outletNo },
                { header: '시설명', value: (r) => r.facilityName },
                { header: '설치 위치', value: (r) => r.installLocation },
                { header: '용량', value: (r) => r.capacity },
                { header: '용량 단위', value: (r) => r.capacityUnit },
                { header: '정격 전력(kW)', value: (r) => r.powerCapacity },
                { header: '상태', value: (r) => (r.isActive ? '사용' : '미사용') },
              ])
            }
          >
            엑셀 내려받기
          </Button>

          {manage && (
            <Button type="primary" onClick={() => setEditing('new')}>
              시설 등록
            </Button>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="여기 등록한 시설이 일지의 표에 자동으로 채워집니다."
        description="시설 구분(대기 배출·대기 방지·폐수 배출·수질 방지)에 따라 어느 일지의 어느 표에 나타날지가 정해집니다. 등록하지 않으면 해당 표가 빈 채로 열립니다."
      />
      <Table<Facility>
        rowKey="facilityId"
        loading={facilities.isLoading}
        dataSource={facilities.data ?? []}
        pagination={false}
        columns={[
          {
            title: '구분',
            dataIndex: 'facilityType',
            width: 140,
            render: (t: Facility['facilityType']) => FACILITY_TYPE_LABELS[t],
          },
          { title: '배출구', dataIndex: 'outletNo', width: 90, render: (v) => v ?? '-' },
          { title: '시설명', dataIndex: 'facilityName' },
          { title: '설치 위치', dataIndex: 'installLocation', render: (v) => v ?? '-' },
          {
            title: '용량',
            width: 140,
            render: (_, row) =>
              row.capacity === null ? '-' : `${row.capacity} ${row.capacityUnit ?? ''}`.trim(),
          },
          {
            title: '정격 전력',
            dataIndex: 'powerCapacity',
            width: 110,
            render: (v: number | null) => (v === null ? '-' : `${v} kW`),
          },
          { title: '오염물질', dataIndex: 'pollutant', width: 120, render: (v) => v ?? '-' },
          {
            title: '상태',
            dataIndex: 'isActive',
            width: 90,
            render: (active: boolean) =>
              active ? <Tag color="green">사용중</Tag> : <Tag>중지</Tag>,
          },
          {
            title: '',
            width: 210,
            render: (_, row) =>
              manage && (
                <Space>
                  <Button size="small" type="link" onClick={() => setEditing(row)}>
                    수정
                  </Button>
                  {row.isActive && (
                    <Popconfirm
                      title="사용 중지"
                      description="작성된 일지는 그대로 남습니다."
                      okText="중지"
                      cancelText="취소"
                      onConfirm={() => deactivate.mutate(row.facilityId)}
                    >
                      <Button size="small" type="link">
                        중지
                      </Button>
                    </Popconfirm>
                  )}
                  <Button
                    size="small"
                    type="link"
                    danger
                    loading={remove.isPending}
                    onClick={() => void askRemove(row)}
                  >
                    삭제
                  </Button>
                </Space>
              ),
          },
        ]}
      />

      <Modal
        open={editing !== null}
        title={
          <Space>
            <span>{editing === 'new' ? '시설 등록' : '시설 수정'}</span>
            <PageHelp title="시설 등록 — 항목 설명">
              <HelpP>
                시설 구분과 시설명만 넣어도 저장됩니다. 나머지는 법정 서식에 인쇄되거나
                일지 작성을 편하게 하는 값입니다.
              </HelpP>
              <HelpList
                items={[
                  {
                    term: '시설 구분',
                    desc: '이 시설이 어느 일지의 어느 표에 나타날지를 정합니다. 가장 중요한 항목입니다.',
                  },
                  {
                    term: '배출구 번호',
                    desc: '대기 시설은 필수입니다. 같은 배출구에 묶인 배출시설과 방지시설이 일지에서 함께 묶입니다.',
                  },
                  { term: '시설명', desc: '일지와 서식에 그대로 찍힙니다.' },
                  { term: '설치 위치', desc: '예: 1공장 옥외. 서식의 「설치장소」 칸입니다.' },
                  { term: '용량 · 단위', desc: '예: 250 m3/min. 서식의 「규격(용량)」 칸입니다.' },
                  { term: '정격 전력(kW)', desc: '방지시설의 전력 사용량 산정 근거입니다.' },
                  { term: '대상 오염물질', desc: '예: 먼지, 황산화물' },
                  {
                    term: '표시 순서',
                    desc: '일지 표에서 위에서 아래로 놓이는 순서입니다. 작은 수가 위입니다.',
                  },
                ]}
              />
            </PageHelp>
          </Space>
        }
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        okText="저장"
        cancelText="취소"
        width={620}
        destroyOnClose
      >
        <Form<FacilityInput>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
          style={{ marginTop: 16 }}
        >
          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item
              name="facilityType"
              label="시설 구분"
              rules={[{ required: true, message: '시설 구분을 고르세요' }]}
              extra="어느 일지의 어느 표에 나타날지가 정해집니다."
              style={{ flex: 1 }}
            >
              <Select
                options={FACILITY_TYPES.map((t) => ({ value: t, label: FACILITY_TYPE_LABELS[t] }))}
              />
            </Form.Item>
            <Form.Item
              name="outletNo"
              label="배출구 번호"
              extra="대기 시설 필수"
              style={{ width: 160 }}
            >
              <Input maxLength={20} placeholder="예: 1" />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="facilityName"
            label="시설명"
            rules={[{ required: true, message: '시설명을 입력하세요' }]}
          >
            <Input maxLength={200} />
          </Form.Item>

          <Form.Item name="installLocation" label="설치 위치">
            <Input maxLength={200} />
          </Form.Item>

          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="capacity" label="용량" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="capacityUnit" label="단위" style={{ width: 130 }}>
              <Input maxLength={20} placeholder="m3/min" />
            </Form.Item>
            <Form.Item name="powerCapacity" label="정격 전력(kW)" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space.Compact>

          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="pollutant" label="대상 오염물질" style={{ flex: 1 }}>
              <Input maxLength={200} placeholder="먼지, 황산화물 등" />
            </Form.Item>
            <Form.Item
              name="sortOrder"
              label="표시 순서"
              extra="작은 수가 위"
              style={{ width: 150 }}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space.Compact>

          <Form.Item name="note" label="비고">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
